import {useCallback, useEffect, useRef} from 'react';

import {beginCriticalWorkflowPainWindow} from '../../../util/datadog/criticalWorkflows/painWindow/observer';
import type {CriticalWorkflowExplicitObservationHandle} from '../../../util/datadog/criticalWorkflows/painWindow/types';

export type LinePlotRenderer = 'react-vis' | 'uplot';

export interface LinePlotHoverMetricContext {
  metricCount: number;
  metricName?: string;
}

interface LinePlotMetricLine {
  aux?: boolean;
  metricName?: string;
}

export const LINE_PLOT_HOVER_MAX_DURATION_MS = 10_000;

const LINE_PLOT_HOVER_WORKFLOW = 'line-plot-hovered';

export function getLinePlotHoverMetricContext(
  lines: readonly LinePlotMetricLine[]
): LinePlotHoverMetricContext {
  const metricNames = new Set<string>();
  for (const line of lines) {
    if (!line.aux && line.metricName != null) {
      metricNames.add(line.metricName);
    }
  }

  if (metricNames.size !== 1) {
    return {metricCount: metricNames.size};
  }
  return {metricCount: 1, metricName: metricNames.values().next().value};
}

// Starts a critical-workflow pain session for uPlot hovers. The session's own
// duration is the hover residence measurement. The pain monitor enforces the
// maximum duration and reports page abandonment, so this hook only starts,
// completes, and cancels the session.
export function useLinePlotHoverSession(
  renderer: LinePlotRenderer,
  metricContext: LinePlotHoverMetricContext
): {onMouseEnter: () => void; onMouseLeave: () => void} {
  const painObservationRef = useRef<
    CriticalWorkflowExplicitObservationHandle | undefined
  >(undefined);
  const rendererRef = useRef(renderer);

  const cancelCurrentSession = useCallback(() => {
    const observation = painObservationRef.current;
    painObservationRef.current = undefined;
    observation?.cancel();
  }, []);

  const onMouseEnter = useCallback(() => {
    cancelCurrentSession();
    if (renderer !== 'uplot') {
      return;
    }
    painObservationRef.current = beginCriticalWorkflowPainWindow({
      workflow: LINE_PLOT_HOVER_WORKFLOW,
      workflowContext: {
        renderer,
        metricCount: metricContext.metricCount,
        ...(metricContext.metricName == null
          ? {}
          : {metricName: metricContext.metricName}),
      },
      maxDurationMs: LINE_PLOT_HOVER_MAX_DURATION_MS,
    });
  }, [cancelCurrentSession, metricContext, renderer]);

  // Callers must not cancel on visibility events; the monitor reports those
  // sessions as abandoned itself, and a later complete() is then a no-op.
  const onMouseLeave = useCallback(() => {
    const observation = painObservationRef.current;
    painObservationRef.current = undefined;
    observation?.complete();
  }, []);

  useEffect(() => {
    if (rendererRef.current !== renderer) {
      cancelCurrentSession();
      rendererRef.current = renderer;
    }
  }, [cancelCurrentSession, renderer]);

  useEffect(() => cancelCurrentSession, [cancelCurrentSession]);

  return {onMouseEnter, onMouseLeave};
}
